import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { SquaresFour, AddressBook, MagnifyingGlass, Plus } from "@phosphor-icons/react";

/**
 * Assign a bare ticket to (a) a table, (b) a loyalty customer, or (c)
 * a free-form name. Extracted from POSPage.jsx (was ~150 lines inline).
 *
 * Props are grouped to avoid a 20-argument signature. ``state`` bundles
 * transient dialog state; ``handlers`` bundles the parent callbacks
 * that mutate parent state.
 */
const AssignTicketDialog = ({
  open,
  onOpenChange,
  state: {
    assignTab,
    setAssignTab,
    tables,
    selectedCustomer,
    setSelectedCustomer,
    ticketName,
    ticketNameDraft,
    setTicketNameDraft,
    setTicketName,
    custSearchQuery,
    setCustSearchQuery,
    newCustForm,
    setNewCustForm,
    assignFilteredCustomers,
  },
  handlers: {
    handleAssignTable,
    handleAssignExistingCustomer,
    handleCreateAndAssignCustomer,
  },
}) => {
  const availableTables = tables.filter((t) => t.status === "open" || t.status === "available");

  const commitName = () => {
    const value = ticketNameDraft.trim();
    if (!value) return;
    setTicketName(value);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="assign-table-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Assign Ticket</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <div className="flex gap-2 mb-4">
            <Button variant={assignTab === "table" ? "default" : "outline"} size="sm" className="flex-1 gap-2" onClick={() => setAssignTab("table")} data-testid="assign-tab-table">
              <SquaresFour size={16} /> Table
            </Button>
            <Button variant={assignTab === "customer" ? "default" : "outline"} size="sm" className="flex-1 gap-2" onClick={() => setAssignTab("customer")} data-testid="assign-tab-customer">
              <AddressBook size={16} /> Customer
            </Button>
            <Button variant={assignTab === "name" ? "default" : "outline"} size="sm" className="flex-1 gap-2" onClick={() => { setAssignTab("name"); setTicketNameDraft(ticketName); }} data-testid="assign-tab-name">
              Name
            </Button>
          </div>

          {assignTab === "table" && (
            <>
              <p className="text-sm text-muted-foreground mb-3">Select a table for this ticket</p>
              {selectedCustomer && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 mb-3 flex items-center gap-2 text-sm">
                  <AddressBook size={14} className="text-primary" weight="fill" />
                  <span className="font-medium text-primary">{selectedCustomer.name}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
                {availableTables.map((table) => (
                  <Button key={table.id} variant="outline" className="h-auto py-3 flex-col items-start text-left" onClick={() => handleAssignTable(table.id)} data-testid={`assign-to-${table.id}`}>
                    <span className="font-medium">{table.name}</span>
                    <span className="text-xs text-muted-foreground">{table.room} - {table.seats} seats</span>
                  </Button>
                ))}
              </div>
              {availableTables.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No available tables</p>
              )}
            </>
          )}

          {assignTab === "name" && (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                Type a name to print on the ticket (for to-go orders, walk-ups, etc.)
              </p>
              <div className="space-y-3">
                <Input
                  autoFocus
                  placeholder="Customer name (e.g. John, To-Go #4)"
                  value={ticketNameDraft}
                  onChange={(e) => setTicketNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitName(); }}
                  data-testid="ticket-name-input"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setTicketName(""); setTicketNameDraft(""); onOpenChange(false); }}
                    data-testid="clear-ticket-name-btn"
                  >
                    Clear
                  </Button>
                  <Button
                    className="flex-1 gap-2"
                    disabled={!ticketNameDraft.trim()}
                    onClick={commitName}
                    data-testid="save-ticket-name-btn"
                  >
                    Save Name
                  </Button>
                </div>
              </div>
            </>
          )}

          {assignTab === "customer" && (
            <>
              <div className="relative mb-3">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search by name or phone..." value={custSearchQuery} onChange={(e) => setCustSearchQuery(e.target.value)} className="pl-9" data-testid="assign-customer-search" />
              </div>
              {selectedCustomer && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 mb-3 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <AddressBook size={14} className="text-primary" weight="fill" />
                    <span className="font-medium text-primary">{selectedCustomer.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)} className="text-xs h-6">Clear</Button>
                </div>
              )}
              <div className="max-h-[180px] overflow-y-auto space-y-1 mb-3">
                {assignFilteredCustomers.map((c) => (
                  <button key={c.id} onClick={() => handleAssignExistingCustomer(c)} className="w-full text-left p-2.5 rounded-lg hover:bg-secondary/50 flex items-center gap-2.5 transition-colors" data-testid={`assign-cust-${c.id}`}>
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <AddressBook size={14} className="text-primary" weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || c.email || "No contact"}</p>
                    </div>
                  </button>
                ))}
                {assignFilteredCustomers.length === 0 && (
                  <p className="text-center text-muted-foreground py-3 text-sm">No customers found</p>
                )}
              </div>
              <Separator className="mb-3" />
              <p className="text-sm font-medium mb-2">New Customer</p>
              <div className="space-y-2">
                <Input placeholder="Name" value={newCustForm.name} onChange={(e) => setNewCustForm({ ...newCustForm, name: e.target.value })} data-testid="new-cust-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Phone" value={newCustForm.phone} onChange={(e) => setNewCustForm({ ...newCustForm, phone: e.target.value })} data-testid="new-cust-phone" />
                  <Input placeholder="Email" value={newCustForm.email} onChange={(e) => setNewCustForm({ ...newCustForm, email: e.target.value })} data-testid="new-cust-email" />
                </div>
                <label className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-colors ${newCustForm.email.trim() ? "border-border bg-secondary/30 hover:bg-secondary/50 cursor-pointer" : "border-border/50 bg-muted/30 cursor-not-allowed opacity-60"}`} data-testid="enroll-loyalty-row">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-primary"
                    checked={newCustForm.enroll_loyalty}
                    onChange={(e) => setNewCustForm({ ...newCustForm, enroll_loyalty: e.target.checked })}
                    disabled={!newCustForm.email.trim()}
                    data-testid="enroll-loyalty-checkbox"
                  />
                  <div className="text-xs flex-1">
                    <div className="flex items-center gap-1 font-medium">
                      <span className="text-amber-500">★</span> Add to Loyalty Program
                    </div>
                    <p className="text-muted-foreground">
                      {newCustForm.email.trim() ? "Earns 1 point per $1 spent" : "Add email to enable"}
                    </p>
                  </div>
                </label>
                <Button onClick={handleCreateAndAssignCustomer} className="w-full gap-2" disabled={!newCustForm.name.trim()} data-testid="create-assign-cust-btn">
                  <Plus size={16} /> Create & Assign
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignTicketDialog;
